import { useState, useRef, useEffect } from "react";
import {
  Calendar,
  Clock,
  AlertCircle,
  ChevronRight,
  CheckCircle,
} from "lucide-react";
import { FormData } from "../App";
import { interviewService } from "../services/interview.service";
import type { CalendarDate, CalendarResponse } from "../types/api.types";

interface Step2Props {
  formData: FormData;
  onUpdate: (data: Partial<FormData>) => void;
  onNext: () => void;
  onBack: () => void;
  userId: number;
}

const WEEKDAYS = [
  "شنبه",
  "یکشنبه",
  "دوشنبه",
  "سه‌شنبه",
  "چهارشنبه",
  "پنج‌شنبه",
  "جمعه",
];

export function Step2Appointment({
  formData,
  onUpdate,
  onNext,
  onBack,
  userId: _userId,
}: Step2Props) {
  const [selectedDate, setSelectedDate] = useState(formData.selectedDate);
  const [selectedTime, setSelectedTime] = useState(formData.selectedTime);
  const [calendarData, setCalendarData] = useState<CalendarResponse | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isBooking, _setIsBooking] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const timeSectionRef = useRef<HTMLDivElement>(null);

  // Fetch calendar data on mount and when returning to this step
  useEffect(() => {
    fetchCalendar();
  }, []); // Always fetch fresh data when step loads

  // Also refetch when component becomes visible (user returns from payment page)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchCalendar(); // Refresh calendar when tab becomes visible
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  // Fetch available slots when date is selected
  useEffect(() => {
    if (selectedDate && calendarData) {
      const dateInfo = calendarData.dates.find((d) => d.date === selectedDate);
      if (dateInfo) {
        setAvailableSlots(dateInfo.available_slots);
      }
    }
  }, [selectedDate, calendarData]);

  const fetchCalendar = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await interviewService.getCalendar();
      setCalendarData(data);
    } catch (err: any) {
      console.error("Error fetching calendar:", err);
      setError(err.message || "خطا در دریافت تقویم. لطفاً دوباره تلاش کنید.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDateSelect = (dateInfo: CalendarDate) => {
    setSelectedDate(dateInfo.date);
    setSelectedTime(""); // Reset time selection
    onUpdate({ selectedDate: dateInfo.date, selectedTime: "" });

    // Smooth scroll to time section
    setTimeout(() => {
      if (timeSectionRef.current) {
        timeSectionRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  const handleTimeSelect = (time: string) => {
    setSelectedTime(time);
    onUpdate({ selectedTime: time });
  };

  // Group dates by month keys returned from API.
  const datesByMonth: { monthKey: string; monthName: string; dates: CalendarDate[] }[] = [];
  if (calendarData?.dates?.length) {
    const byMonth = new Map<string, CalendarDate[]>();
    for (const d of calendarData.dates) {
      const key = d.month ?? (d.date ? d.date.slice(0, 7) : "");
      if (!key) continue;
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key)!.push(d);
    }
    for (const [key, list] of byMonth) {
      datesByMonth.push({
        monthKey: key,
        monthName: list[0]?.month_name ?? key,
        dates: [...list].sort((a, b) => a.day_num - b.day_num),
      });
    }
    datesByMonth.sort((a, b) => a.monthKey.localeCompare(b.monthKey));
  }

  function isHolidayForDate(dateInfo: CalendarDate): boolean {
    return Boolean(dateInfo.is_holiday) || Boolean(dateInfo.is_friday);
  }

  // Resolve display status from API data only.
  function getDateDisplayStatus(dateInfo: CalendarDate): "available" | "unavailable" | "fully_booked" {
    if (dateInfo.date_status) return dateInfo.date_status;
    const isHoliday = isHolidayForDate(dateInfo);
    if (isHoliday) return "unavailable";
    if (dateInfo.fully_booked) return "fully_booked";
    return "available";
  }

  function buildVisibleWeeksForMonth(
    sortedDates: CalendarDate[],
  ) {
    const firstDate = sortedDates[0];
    const paddingCount = firstDate ? WEEKDAYS.indexOf(firstDate.day) : 0;
    const allDays = [...Array(paddingCount).fill(null), ...sortedDates];
    const weeks: (CalendarDate | null)[][] = [];
    for (let i = 0; i < allDays.length; i += 7) {
      const week = allDays.slice(i, i + 7);
      if (week.length < 7) {
        week.push(...Array(7 - week.length).fill(null));
      }
      weeks.push(week);
    }
    return weeks.filter((week) =>
      week.some(
        (day) =>
          day !== null &&
          !day.is_past &&
          getDateDisplayStatus(day as CalendarDate) === "available",
      ),
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDate || !selectedTime) {
      alert("لطفاً تاریخ و زمان مصاحبه را انتخاب کنید.");
      return;
    }

    // Go to step 3 for confirmation
    onNext();
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
        <div
          className="animate-spin rounded-full h-16 w-16 border-b-2 mx-auto mb-4"
          style={{ borderColor: "var(--primary-gold)" }}
        ></div>
        <p className="text-lg" style={{ color: "var(--text-gray)" }}>
          در حال بارگذاری تقویم...
        </p>
      </div>
    );
  }

  // Show error state with retry
  if (error && !calendarData) {
    return (
      <div className="bg-white rounded-2xl shadow-sm p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-8 h-8 text-red-500" />
        </div>
        <h3
          className="text-xl mb-4"
          style={{ color: "var(--secondary-green)" }}
        >
          خطا در بارگذاری تقویم
        </h3>
        <p className="text-lg mb-6" style={{ color: "var(--text-gray)" }}>
          {error}
        </p>
        <div className="flex gap-4 justify-center">
          <button
            onClick={fetchCalendar}
            className="px-6 py-3 rounded-xl"
            style={{
              backgroundColor: "var(--primary-gold)",
              color: "var(--secondary-green)",
            }}
          >
            تلاش مجدد
          </button>
          <button
            onClick={onBack}
            className="px-6 py-3 rounded-xl border-2"
            style={{
              borderColor: "var(--border-light)",
              color: "var(--text-gray)",
            }}
          >
            بازگشت
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Availability Legend */}
      <div className="bg-white rounded-xl p-4 flex items-center justify-center gap-4 sm:gap-6 flex-wrap text-sm">
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded border-2"
            style={{
              borderColor: "var(--primary-gold)",
              backgroundColor: "var(--primary-gold)",
            }}
          ></div>
          <span style={{ color: "var(--text-gray)" }}>انتخاب شده</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded border-2"
            style={{
              borderColor: "var(--border-light)",
              backgroundColor: "white",
            }}
          ></div>
          <span style={{ color: "var(--text-gray)" }}>در دسترس</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded border-2 opacity-40"
            style={{ borderColor: "#9CA3AF", backgroundColor: "#F3F4F6" }}
          ></div>
          <span style={{ color: "var(--text-gray)" }}>تعطیل / غیرفعال</span>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="w-6 h-6 rounded border-2 opacity-40"
            style={{ borderColor: "#D1D1D1", backgroundColor: "#F5F5F5" }}
          ></div>
          <span style={{ color: "var(--text-gray)" }}>پر</span>
        </div>
      </div>

      {/* Date Selection: one section per month from API */}
      {datesByMonth.map(({ monthKey, monthName, dates: monthDates }) => {
        const visibleWeeks = buildVisibleWeeksForMonth(monthDates);
        return (
          <div
            key={monthKey}
            className="bg-white rounded-2xl shadow-sm p-6 sm:p-8"
          >
            <div
              className="flex items-center gap-3 mb-6 pb-4 border-b-2"
              style={{ borderColor: "var(--border-light)" }}
            >
              <div
                className="p-2 rounded-lg"
                style={{ backgroundColor: "#F0F9F8" }}
              >
                <Calendar
                  className="w-6 h-6"
                  style={{ color: "var(--secondary-green)" }}
                />
              </div>
              <h2
                className="text-2xl"
                style={{ color: "var(--secondary-green)" }}
              >
                انتخاب تاریخ مصاحبه - ماه {monthName}
              </h2>
            </div>

            <div
              className="grid grid-cols-7 gap-1 sm:gap-3 mb-4"
              style={{ direction: "rtl" }}
            >
              {WEEKDAYS.map((day) => (
                <div
                  key={day}
                  className="text-center text-[10px] sm:text-sm font-bold py-2 opacity-60"
                  style={{ color: "var(--text-gray)" }}
                >
                  {day}
                </div>
              ))}

              {visibleWeeks.map((week, weekIndex) => (
                <div
                  key={`${monthKey}-week-${weekIndex}`}
                  className="contents"
                >
                  {week.map((dateInfo, dayIndex) => {
                    if (!dateInfo || dateInfo.is_past) {
                      return (
                        <div
                          key={
                            dateInfo
                              ? dateInfo.date
                              : `empty-${monthKey}-${weekIndex}-${dayIndex}`
                          }
                          className="p-1 sm:p-4"
                        />
                      );
                    }

                    const isSelected = selectedDate === dateInfo.date;
                    const displayStatus = getDateDisplayStatus(dateInfo);
                    const isAvailable = displayStatus === "available";
                    const isHoliday = isHolidayForDate(dateInfo);

                    return (
                      <button
                        key={dateInfo.date}
                        type="button"
                        onClick={() =>
                          isAvailable ? handleDateSelect(dateInfo) : undefined
                        }
                        disabled={!isAvailable}
                        className={`p-1 sm:p-4 rounded-xl border-2 transition-all relative min-h-[50px] sm:min-h-[80px] flex flex-col items-center justify-center ${
                          !isAvailable
                            ? "cursor-not-allowed opacity-40"
                            : "hover:shadow-md cursor-pointer"
                        } ${isSelected ? "shadow-md" : ""}`}
                        style={{
                          backgroundColor: isSelected
                            ? "var(--primary-gold)"
                            : isAvailable
                              ? "white"
                              : "#F5F5F5",
                          borderColor: isSelected
                            ? "var(--primary-gold)"
                            : isAvailable
                              ? "var(--border-light)"
                              : "#D1D1D1",
                          color: isSelected
                            ? "var(--secondary-green)"
                            : !isAvailable
                              ? "#999"
                              : "var(--text-dark)",
                        }}
                      >
                        <div className="text-[10px] sm:text-sm opacity-70 hidden sm:block">
                          {dateInfo.day}
                        </div>
                        <div className="text-sm sm:text-lg font-bold">
                          {dateInfo.day_num}
                        </div>
                        {displayStatus === "unavailable" && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-[8px] sm:text-xs bg-gray-200 text-gray-600 px-1 sm:px-2 py-0.5 rounded-md">
                              {isHoliday ? "تعطیل" : "غیرفعال"}
                            </span>
                          </div>
                        )}
                        {displayStatus === "fully_booked" && (
                          <div className="absolute inset-0 flex items-center justify-center">
                            <span className="text-[8px] sm:text-xs bg-red-100 text-red-600 px-1 sm:px-2 py-0.5 rounded-md">
                              پر
                            </span>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Time Selection */}
      {selectedDate && availableSlots.length > 0 && (
        <div
          ref={timeSectionRef}
          className="bg-white rounded-2xl shadow-sm p-6 sm:p-8 animate-fadeIn"
          style={{
            animation: "fadeIn 0.5s ease-in-out",
            border: "2px solid var(--primary-gold)",
          }}
        >
          <div
            className="flex items-center gap-3 mb-6 pb-4 border-b-2"
            style={{ borderColor: "var(--border-light)" }}
          >
            <div
              className="p-2 rounded-lg"
              style={{ backgroundColor: "#FEF9EC" }}
            >
              <Clock
                className="w-6 h-6"
                style={{ color: "var(--primary-gold)" }}
              />
            </div>
            <h2
              className="text-2xl"
              style={{ color: "var(--secondary-green)" }}
            >
              انتخاب زمان مصاحبه
            </h2>
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-3">
            {availableSlots.map((time) => {
              const isSelected = selectedTime === time;

              return (
                <button
                  key={time}
                  type="button"
                  onClick={() => handleTimeSelect(time)}
                  className={`px-4 py-3 rounded-xl border-2 transition-all hover:shadow-md cursor-pointer ${
                    isSelected ? "shadow-md" : ""
                  }`}
                  style={{
                    backgroundColor: isSelected
                      ? "var(--secondary-green)"
                      : "white",
                    borderColor: isSelected
                      ? "var(--secondary-green)"
                      : "var(--border-light)",
                    color: isSelected ? "white" : "var(--text-dark)",
                  }}
                >
                  {time}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* No slots available message */}
      {selectedDate && availableSlots.length === 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-600 mx-auto mb-3" />
          <p className="text-lg text-yellow-800">
            متأسفانه تمام زمان‌های این تاریخ رزرو شده است. لطفاً تاریخ دیگری
            انتخاب کنید.
          </p>
        </div>
      )}

      {/* Payment Alert */}
      {selectedDate && selectedTime && (
        <div
          className="rounded-2xl p-6 border-2 flex items-start gap-4 animate-fadeIn"
          style={{
            backgroundColor: "#FEF9EC",
            borderColor: "var(--primary-gold)",
            animation: "fadeIn 0.5s ease-in-out",
          }}
        >
          <AlertCircle
            className="w-6 h-6 flex-shrink-0 mt-0.5"
            style={{ color: "var(--secondary-green)" }}
          />
          <div>
            <h3
              className="mb-2 text-lg"
              style={{ color: "var(--secondary-green)" }}
            >
              اطلاعات پرداخت
            </h3>
            <p
              className="leading-relaxed"
              style={{ color: "var(--secondary-green)" }}
            >
              برای تکمیل پیش‌ثبت‌نام، پرداخت <strong>۱۵۰,۰۰۰ تومان</strong>{" "}
              الزامی است.
              <br />
              مبلغ ۱۵۰ هزار تومان پرداختی، به‌منزله تعهد حضور است. در صورت عدم
              تأیید دانش‌آموز، این مبلغ به‌طور کامل بازگردانده می‌شود و در صورت
              تأیید، به‌عنوان بخشی از شهریه محاسبه خواهد شد.
              <br />
              پس از کلیک روی دکمه تایید، به درگاه پرداخت منتقل خواهید شد.
            </p>
          </div>
        </div>
      )}

      {/* Booking Summary */}
      {selectedDate && selectedTime && (
        <div
          className="bg-white rounded-2xl shadow-md p-6 border-2 animate-fadeIn"
          style={{
            borderColor: "var(--secondary-green)",
            animation: "fadeIn 0.5s ease-in-out",
          }}
        >
          <div className="flex items-center gap-2 mb-4">
            <CheckCircle
              className="w-6 h-6"
              style={{ color: "var(--secondary-green)" }}
            />
            <h3 className="text-xl" style={{ color: "var(--secondary-green)" }}>
              خلاصه رزرو شما
            </h3>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div
              className="flex items-center gap-3 p-4 rounded-xl"
              style={{ backgroundColor: "#F0F9F8" }}
            >
              <Calendar
                className="w-5 h-5"
                style={{ color: "var(--secondary-green)" }}
              />
              <div>
                <div className="text-sm" style={{ color: "var(--text-gray)" }}>
                  تاریخ انتخاب شده
                </div>
                <div className="text-lg" style={{ color: "var(--text-dark)" }}>
                  {selectedDate}
                </div>
              </div>
            </div>
            <div
              className="flex items-center gap-3 p-4 rounded-xl"
              style={{ backgroundColor: "#FEF9EC" }}
            >
              <Clock
                className="w-5 h-5"
                style={{ color: "var(--primary-gold)" }}
              />
              <div>
                <div className="text-sm" style={{ color: "var(--text-gray)" }}>
                  ساعت انتخاب شده
                </div>
                <div className="text-lg" style={{ color: "var(--text-dark)" }}>
                  {selectedTime}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <button
          type="button"
          onClick={onBack}
          disabled={isBooking}
          className="px-6 py-3 rounded-xl border-2 transition-all hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50"
          style={{
            borderColor: "var(--border-light)",
            color: "var(--text-gray)",
          }}
        >
          <ChevronRight className="w-5 h-5" />
          بازگشت به ویرایش اطلاعات
        </button>

        <button
          type="submit"
          disabled={!selectedDate || !selectedTime}
          className={`px-8 py-4 rounded-xl text-lg transition-all shadow-md ${
            selectedDate && selectedTime
              ? "hover:shadow-lg transform hover:-translate-y-0.5"
              : "opacity-50 cursor-not-allowed"
          }`}
          style={{
            backgroundColor: "var(--primary-gold)",
            color: "var(--secondary-green)",
          }}
        >
          ← ادامه به تایید نهایی
        </button>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </form>
  );
}
